import { patientApi } from '../api/patientApi';
import { 
  Patient, 
  PatientFormData, 
  PatientListResponse, 
  PatientFilterParams 
} from '../types/patient.types';

/**
 * Service for handling patient-related business logic
 * This layer can be used to add additional business logic or transformations
 * before/after API calls
 */
export const PatientService = {
  /**
   * Get all patients with pagination and filters
   * @param params Filter and pagination parameters
   * @returns Paginated list of patients
   */
  async getPatients(params: PatientFilterParams): Promise<PatientListResponse> {
    return patientApi.getPatients(params);
  },

  /**
   * Get a single patient by ID
   * @param id Patient ID
   * @returns Patient details
   */
  async getPatientById(id: string): Promise<Patient> {
    return patientApi.getPatientById(id);
  },

  /**
   * Create a new patient
   * @param data Patient data
   * @returns Created patient
   */
  async createPatient(data: PatientFormData): Promise<Patient> {
    return patientApi.createPatient(data);
  },

  /**
   * Update an existing patient
   * @param id Patient ID
   * @param data Updated patient data
   * @returns Updated patient
   */
  async updatePatient(id: string, data: Partial<PatientFormData>): Promise<Patient> {
    return patientApi.updatePatient(id, data as PatientFormData);
  },

  /**
   * Delete a patient (soft delete)
   * @param id Patient ID
   */
  async deletePatient(id: string): Promise<void> {
    await patientApi.deletePatient(id);
  },

  /**
   * Toggle patient active status
   * @param id Patient ID
   * @param isActive New status
   * @returns Updated patient
   */
  async togglePatientStatus(id: string, isActive: boolean): Promise<Patient> {
    return patientApi.togglePatientStatus(id, isActive);
  },

  /**
   * Search patients by name, MRN, or contact info
   * @param query Search query
   * @returns List of matching patients
   */
  async searchPatients(query: string): Promise<Patient[]> {
    return patientApi.searchPatients(query);
  },

  /**
   * Check if an MRN already exists
   * @param mrn Medical Record Number to check
   * @returns Boolean indicating if MRN exists
   */
  async checkMrnExists(mrn: string): Promise<boolean> {
    return patientApi.checkMrnExists(mrn);
  },

  /**
   * Check if a phone number already exists
   * @param phoneNumber Phone number to check
   * @returns Array of patients with matching phone number
   */
  async checkPhoneExists(phoneNumber: string): Promise<Patient[]> {
    return patientApi.checkPhoneExists(phoneNumber);
  }
};
